import {
    Editor,
    Element as SlateElement,
    Range as SlateRange,
    Transforms,
    type Descendant,
    type MathEditorElement,
    type Node as SlateNode,
} from "slate";
import {ReactEditor} from "slate-react";

import {isIFrame} from "@khan/guide-core-helpers";
import type {ReferencesContext} from "@khan/slate-contexts";

/**
 * Parse the Slate editor value into a string for Khanmigo.
 */
export const parseMessage = (value: any): string => {
    const messageArray: Array<string> = [];

    value.forEach((paragraph: SlateElement) => {
        const paragraphArray: Array<string> = [];
        if (paragraph.children) {
            paragraph.children.forEach((node: Descendant) => {
                if (node["text"]) {
                    paragraphArray.push(node["text"]);
                } else if (node["tex"]) {
                    // math
                    value = `\\(` + node["tex"] + `\\)`;
                    paragraphArray.push(value);
                } else if (node["ref"]) {
                    // reference
                    paragraphArray.push(node["ref"]);
                }
            });
        }
        messageArray.push(paragraphArray.join(" "));
    });

    return messageArray.join("\r\n");
};

export const onSlateChange = (
    editor: Editor,
    references: React.ContextType<typeof ReferencesContext>,
) => {
    // if the user can see an active references button, allow reference check
    if (references.showButton && !references.disabled) {
        processReferences(editor, references);
    }
};

const processReferences = (
    editor: Editor,
    references: React.ContextType<typeof ReferencesContext>,
) => {
    const {selection} = editor;
    const {setQuery} = references;

    const regex = /@(\w*)$/;

    // If the selection is a cursor (collapsed), look backwards to see if there
    //   is a word that starts with @. If so, activate the reference search.
    // This section adapted from the official slatejs example:
    //   https://github.com/ianstormtaylor/slate/blob/a81f76f3a328869a20cc45cb581f6129eba7ccea/site/examples/mentions.tsx#L80C5-L80C5
    if (selection && SlateRange.isCollapsed(selection)) {
        const [start] = SlateRange.edges(selection);
        const wordBefore = Editor.before(editor, start, {unit: "word"});
        // Get one character before the "word" in case it's "@".
        const before =
            wordBefore && (Editor.before(editor, wordBefore) || wordBefore);
        const beforeRange = before && Editor.range(editor, before, start);
        const beforeText = beforeRange && Editor.string(editor, beforeRange);
        const beforeMatch = beforeText && beforeText.match(regex);

        if (beforeMatch) {
            // When there is no search (user selects from list), sometimes the
            //   previous characters are overwritten. We need to move the range
            //   to just the @ symbol.
            // `beforeMatch` looks like ["@search", "search"]
            const range = !beforeMatch[1]
                ? {
                      ...beforeRange,
                      anchor: {
                          path: beforeRange.focus.path,
                          offset: beforeRange.focus.offset - 1,
                      },
                  }
                : beforeRange;

            setQuery({
                search: beforeMatch[1],
                range: range,
            });
            return;
        }
    }

    setQuery({
        search: "",
        range: null,
    });
};

// In order to create a dynamic keydown handler that is handled in one place,
//   define a config object for every feature that we can merge in ChatInput.
export interface KeyDownArgs {
    event: React.KeyboardEvent<HTMLDivElement>;
    editor?: Editor;
    references?: React.ContextType<typeof ReferencesContext>;
}
export type KeyDownConfig = Record<string, (args: KeyDownArgs) => void>;
export const slateKeyDownConfig: KeyDownConfig = {
    // https://github.com/ianstormtaylor/slate/blob/main/site/examples/inlines.tsx#L79C1-L84C91
    // Default left/right behavior is unit:'character'.
    // This fails to distinguish between two cursor positions, such as
    // <inline>foo<cursor/></inline> vs <inline>foo</inline><cursor/>.
    // Here we modify the behavior to unit:'offset'.
    // This lets the user step into and out of the inline without stepping over characters.
    // You may wish to customize this further to only use unit:'offset' in specific cases.
    ["ArrowLeft"]: ({event, editor}) => {
        if (!editor) {
            return;
        }
        const {selection} = editor;
        if (selection && SlateRange.isCollapsed(selection)) {
            const {key} = event;

            if (key === "ArrowLeft") {
                event.preventDefault();
                Transforms.move(editor, {
                    unit: "offset",
                    reverse: true,
                });
                return;
            }
        }
    },
    ["ArrowRight"]: ({event, editor}) => {
        if (!editor) {
            return;
        }
        const {selection} = editor;
        if (selection && SlateRange.isCollapsed(selection)) {
            const {key} = event;

            if (key === "ArrowRight") {
                event.preventDefault();
                Transforms.move(editor, {unit: "offset"});
                return;
            }
        }
    },
};

export const isMathEditor = (node: SlateNode): node is MathEditorElement =>
    (node as unknown as MathEditorElement).type === "math-editor";

/**
 * Insert a math editor element at the current selection.
 */
export const insertMath = (editor: Editor) => {
    if (editor.selection) {
        wrapMath(editor);
    }
};

/**
 * Wrap the current selection in a math editor element.
 */
const wrapMath = (editor: Editor) => {
    const {selection} = editor;

    const math: MathEditorElement = {
        type: "math-editor",
        children: [{text: ""}],
        // BaseSelection = Range | null
        // so null check first to use in Editor.string() which expects Range
        tex: selection ? Editor.string(editor, selection) : "",
    };

    // To ensure there is a space before the math editor, insert a space if (a) the current selection
    // is not at the start of the chat input and (b) there is currently no space before the selection.
    if (selection) {
        const [start] = SlateRange.edges(selection);
        const characterBefore = Editor.before(editor, start, {
            unit: "character",
        });
        const beforeRange =
            characterBefore && Editor.range(editor, characterBefore, start);
        const beforeText = beforeRange && Editor.string(editor, beforeRange);

        if (beforeText !== " " && beforeText !== undefined) {
            // If we're already focused on a math editor, move the cursor forward
            // so that we can properly insert the required space.
            if (isMathEditorSelected(editor)) {
                Transforms.move(editor, {distance: 1, unit: "character"});
            }
            Transforms.insertText(editor, " ");
        }
    }

    // Insert the math editor
    Transforms.insertNodes(editor, math);
};

const isMathEditorSelected = (editor: Editor): boolean => {
    const [node] = Editor.nodes(editor, {
        match: (n) =>
            SlateElement.isElement(n) &&
            (n as SlateElement).type === "math-editor",
    });

    return !!node;
};

export const insertReference = (
    editor: Editor,
    ref: string,
    range: SlateRange | null | undefined,
) => {
    if (range) {
        Transforms.select(editor, range);
        Transforms.insertNodes(editor, {
            type: "reference",
            ref,
            children: [{text: ""}],
        });
        Transforms.move(editor);
    }
};

export const scrollSelectionIntoViewOverride = (
    editor: ReactEditor,
    domRange: Range, // DOMRange
) => {
    // This is a workaround for a bug in which Slate is triggering a scroll
    //   event when the user types a number or symbol.
    // Overrides:
    // https://github.com/ianstormtaylor/slate/blob/70166d6386aa8ee33eddc6134262c1035237ee36/packages/slate-react/src/components/editable.tsx#L1757
    if (
        // we do NOT want this behavior for the canvas iFrame
        // we do however need it for all other khanmigo interactions like this
        !isIFrame() &&
        (!editor.selection ||
            (editor.selection && SlateRange.isCollapsed(editor.selection)))
    ) {
        domRange.startContainer.parentElement?.scrollIntoView?.();
    }
};

export const resetEditor = (editor: Editor, focus: boolean = true) => {
    Transforms.delete(editor, {
        at: {
            anchor: Editor.start(editor, []),
            focus: Editor.end(editor, []),
        },
    });
    editor.history = {redos: [], undos: []};

    // Sets the cursor to the beginning of the editor and focuses it.
    Transforms.select(editor, Editor.start(editor, []));

    if (focus) {
        ReactEditor.focus(editor);
    }
};
